import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const Home = () => {
  const heroRef = useRef(null);
  const heroScrollLineRef = useRef(null);
  const heroBackgroundRef = useRef(null);
  const homeBackgroundRef = useRef(null);
  const featuresBackgroundRef = useRef(null);
  const card1BackgroundRef = useRef(null);
  const card2BackgroundRef = useRef(null);
  const card3BackgroundRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset;

      // Hero background parallax
      if (heroBackgroundRef.current) {
        const parallax = scrolled * 0.5;
        heroBackgroundRef.current.style.transform = `translateY(${parallax}px)`;
      }

      // Hero-only scroll line (Option B): grows upward from hero bottom while user scrolls through hero.
      if (heroRef.current && heroScrollLineRef.current) {
        const heroTop = heroRef.current.getBoundingClientRect().top + window.pageYOffset;
        const heroHeight = heroRef.current.offsetHeight || 1;
        const progress = Math.max(0, Math.min(1, (scrolled - heroTop) / heroHeight));
        heroScrollLineRef.current.style.transform = `scaleY(${progress})`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="home">
      <div
        className="home-background"
        ref={homeBackgroundRef}
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/topo_map.png)`
        }}
        aria-hidden="true"
      />
      <section ref={heroRef} className="hero">
        <div 
          ref={heroBackgroundRef}
          className="hero-background"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/hands.jpeg)`
          }}
        ></div>
        <div className="hero-scroll-line-container" aria-hidden="true">
          <div className="hero-scroll-line" ref={heroScrollLineRef} />
        </div>
        <div className="hero-content">
          <img
            src="/images/uni_compass.svg"
            alt="UpNorth Impact Logo"
            className="hero-logo"
            loading="eager"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <h1 className="hero-title">Helping you find your path</h1>
          <h2 className="hero-tagline">Expose. Educate. Engage.</h2>
          <p className="hero-subtitle">
            UpNorth Impact provides comprehensive philanthropic advisory and management consulting for donors, foundations, and nonprofits.
          </p>
          <div className="hero-buttons">
            <Link to="/services" className="btn btn-primary">Our Services</Link>
            <Link to="/contact" className="btn btn-secondary">Get Started</Link>
          </div>
        </div>
      </section>

      <section className="features">
        <div
          className="features-background"
          ref={featuresBackgroundRef}
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/topo_map.png)`
          }}
          aria-hidden="true"
        />
        <div className="container">
          <h2 className="section-title">Our Core Mission</h2>
          <p className="mission-text">
            We partner with you to build a more strategic and elevated approach to your giving to drive meaningful and lasting change.
          </p>
          <div className="features-grid">
            <div className="feature-card feature-card-1">
              <div 
                ref={card1BackgroundRef}
                className="feature-card-background"
                style={{
                  backgroundImage: `url(${process.env.PUBLIC_URL}/images/compass_cloth.jpg)`
                }}
              ></div>
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6L12 2L21 6V18L12 22L3 18V6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 6L12 10L21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 18L12 14L21 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Expose.</h3>
              <p>Develop comprehensive strategies that align with your mission and drive measurable outcomes.</p>
            </div>
            <div className="feature-card feature-card-2">
              <div 
                ref={card2BackgroundRef}
                className="feature-card-background"
                style={{
                  backgroundImage: `url(${process.env.PUBLIC_URL}/images/map.jpg)`
                }}
              ></div>
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 6V22L8 18L16 22L23 18V2L16 6L8 2L1 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 2V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 6V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Educate.</h3>
              <p>Leverage years of experience working with non-profits of all sizes and sectors.</p>
            </div>
            <div className="feature-card feature-card-3">
              <div 
                ref={card3BackgroundRef}
                className="feature-card-background"
                style={{
                  backgroundImage: `url(${process.env.PUBLIC_URL}/images/road_arrows.jpg)`
                }}
              ></div>
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 4.15893 5.32387 6.12132 3.76142C8.08371 2.19897 10.5837 1.5 12 1.5C13.4163 1.5 15.9163 2.19897 17.8787 3.76142C19.8411 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h3>Engage.</h3>
              <p>We work alongside your team as trusted partners, not just consultants.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Amplify Your Impact?</h2>
            <p>Let's work together to create meaningful change in your community.</p>
            <Link to="/contact" className="btn btn-primary btn-large">Schedule a Consultation</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
